import { Modal, Button, useNotify } from '@hotjar/hotsauce';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/browser';
import { Crosstab } from '@hotjar/webapp/utils/crosstab';
import { sendRecordingsBulkDeletion } from '@hotjar/recordings/src/features/RecordingsPage/api/sendRecordingsBulkDeletion';
import type { Recording } from '../../types';
import { ModalStateEnum } from '../../types';
import { useModalStore } from '../../store/modalStore';

interface ModalProps {
  recording: Recording;
  isPlaying: boolean;
  onPause: () => void;
  onPlay: () => void;
}

export const DeleteRecordingModal = ({
  recording,
  isPlaying,
  onPause,
  onPlay,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { showToast } = useNotify();
  const [wasPlaying, setWasPlaying] = useState(false);
  const activeModal = useModalStore((state) => state.activeModal);
  const { setActiveModal } = useModalStore.getState();
  const closeModal = () => setActiveModal(ModalStateEnum.CLOSE);
  const pauseRecordingIfNeeded = useCallback(() => {
    setWasPlaying(isPlaying);
    if (isPlaying) {
      onPause();
    }
  }, [isPlaying, onPause]);
  const resetRecordingPlayState = useCallback(() => {
    if (wasPlaying) {
      onPlay();
    }
  }, [wasPlaying, onPlay]);
  const doDeleteRecording = async () => {
    const { id, created_epoch_time: created, site_id: siteId } = recording;
    try {
      const wasDeleted = await sendRecordingsBulkDeletion([{ id, created }], siteId);

      if (wasDeleted) {
        Crosstab.signal(
          'recordingDeleted',
          {
            recordingDeletedFromThisTab: true,
            recordingId: id,
          },
          true,
        );
        closeModal();
      } else {
        showToast({
          kind: 'danger',
          text: t('common:player.recording-not-deleted'),
        });
      }
    } catch (responseError) {
      showToast({ kind: 'danger', text: t('common:player.recording-not-deleted') });
      Sentry.captureMessage('Error deleting recording', Sentry.Severity.Error);
      Sentry.captureException(responseError);
    }
  };

  return (
    <Modal
      appElementSelector="body"
      contentLabel="Delete recording"
      isOpen={activeModal === ModalStateEnum.DELETE_RECORDING_OPEN}
      onClose={closeModal}
      className="keyboard-shortcut-modal"
      onAfterOpen={pauseRecordingIfNeeded}
      onAfterClose={resetRecordingPlayState}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
    >
      <Modal.Header title={t('common:player.delete-recording-question')} />

      <Modal.Body>
        <p>{t('common:player.recording-delete-warning')}</p>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={closeModal} kind="ghost">
          {t('common:player.cancel')}
        </Button>
        <Button onClick={doDeleteRecording} kind="danger">
          {t('common:player.recording-delete-confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
